import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import AsyncCreatableSelect from "react-select/async-creatable";
import _ from "lodash";
import { MovieAdviceService } from "../../services/MovieAdviceService";
import { ConfirmDialog } from "./ConfirmDialog";

const AdviceSelect = ({ onChange, selectedValue }) => {
  const adviceService = useMemo(() => new MovieAdviceService(), []);
  const [pendingAdvice, setPendingAdvice] = useState(null);

  const search = async (name = "") => {
    const params = {
      name,
      maxResults: 999,
      startAt: 0,
    };
    return (await adviceService.list(params)).data;
  };

  const loadOptionHandle = (name, callback) => {
    (async () => {
      const { data } = await search(name);
      const options = data
        .map((item) => ({ value: item.id, label: item.name.trimStart() }))
        .sort((a, b) => a.label.localeCompare(b.label));
      callback(options);
    })();
  };

  const create = async (name) => {
    const { data } = await adviceService.post({
      name,
      label: name,
    });
    return { label: data.name, value: data.id };
  };

  const ignoredWords = ["and", "or", "of", "the", "with"];
  const similarityThreshold = 0.75;

  const toWords = (name) =>
    new Set(
      ((name ?? "").toLowerCase().match(/[a-z0-9]+/g) ?? [])
        .filter((word) => !ignoredWords.includes(word))
        .map((word) => (word.length > 3 ? word.replace(/s$/, "") : word))
    );

  const findSimilarAdviceNames = async (name) => {
    const { data } = await search();
    const inputWords = toWords(name);
    const similarAdvices = data.filter((item) => {
      const existingWords = toWords(item.name);
      const commonWordCount = [...existingWords].filter((word) =>
        inputWords.has(word)
      ).length;

      // Compare against the shorter name so a name contained in the other still counts as similar,
      // e.g. "Coarse language" vs "Mild coarse language" shares 2 of 2 words
      const shorterNameWordCount = Math.min(existingWords.size, inputWords.size);

      return (
        shorterNameWordCount > 0 &&
        commonWordCount / shorterNameWordCount >= similarityThreshold
      );
    });
    return similarAdvices.map((item) => item.name.trim()).sort((a, b) => a.localeCompare(b));
  };

  const similarAdviceMessage = pendingAdvice
    ? "Similar consumer advice(s) already exists. Do you still want to proceed?" +
      `<ul class="list-unstyled mt-2 mb-0">${pendingAdvice.similarAdviceNames
        .map((name) => `<li>${_.escape(name)}</li>`)
        .join("")}</ul>`
    : "";

  const onSelect = async (value, actionMeta) => {
    if (actionMeta.action !== "create-option") {
      onChange({ ...value });
      return;
    }
    const similarAdviceNames = await findSimilarAdviceNames(value.label);
    if (similarAdviceNames.length > 0) {
      setPendingAdvice({ name: value.label, similarAdviceNames });
      return;
    }
    onChange(await create(value.label));
  };

  const onConfirmCreate = async () => {
    const { name } = pendingAdvice;
    setPendingAdvice(null);
    onChange(await create(name));
  };

  return (
    <>
      <AsyncCreatableSelect
        isClearable
        defaultOptions={true}
        loadOptions={loadOptionHandle}
        onChange={onSelect}
        value={selectedValue}
      />
      {createPortal(
        <div style={{ position: "relative", zIndex: 1060 }}>
          <ConfirmDialog
            visible={!!pendingAdvice}
            title="Similar Consumer Advice Found"
            message={similarAdviceMessage}
            onProceed={onConfirmCreate}
            onCancel={() => setPendingAdvice(null)}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default AdviceSelect;
